import
{
	PageMobileConfigType,
	PageStepType,
	SearchResultSheets,
	SearchResultTable,
} from './PageTypes';
import { PageDataType } from './PageTypes';

import { PageEngine } from './PageEngine';
import { TaskManager } from '../Tasks/TaskManager';
import { LogicEngine } from '../LogicEngine/LogicEngine';

import { DataQuery } from '../utils/DataQuery';
import { DataRecord } from '../utils/DataRecord';
import { QueryLog } from '../Participant/QueryLog';
import { QueryLogType } from '../Participant/QueryLogTypes';
import { DataTools } from '../utils/DataTools';

export class PageParser
{
	static last_task_id: any = undefined;

	rawConfigFile: PageMobileConfigType;

	constructor(rawData: any)
	{
		this.rawConfigFile = rawData as PageMobileConfigType;
		this.parseRawConfig();
	}

	/**
	 * Parse the configuration file to pre-process the data
	 */
	parseRawConfig() { }

	/** Returns a list of all available routes in a given json file */
	getRouteList(): string[]
	{
		let results: string[] = [];
		for (let tableName in this.rawConfigFile.tables)
		{
			for (let tableData of this.rawConfigFile.tables[tableName])
			{
				if (!tableData.meta || !tableData.meta.route) continue;
				results.push(tableData.meta.route);
			}
		}

		return results;
	}

	/**
	 * Given a Query record, find the associated edit route
	 * @param query_data A DataRecord loaded from Query table
	 * @returns the full path to edit or undefined if not available
	 */
	async findEditLink(query_id: string): Promise<string>
	{
		// The query_id is in the Query data table, start by loading that
		// record so that we can find the reference_task_id or other data to help
		// locate the edit table.
		const dataQuery = DataQuery.instance();
		const query_data: DataRecord<QueryLog> | null = await dataQuery.executeQueryById(
			'Query',
			query_id,
		);

		if (!query_data)
		{
			console.warn('Warning: PageParser findEditLink missing query record for id ', query_id);
			throw 'Unable to continue, missing findEditLink ID ' + query_id;
		}

		// Records that are based on a task are simpler to edit.  They have an associated task page
		if (query_data.data.reference_task_id)
		{
			// Load the associated task
			let find = await dataQuery.executeQueryById('Tasks', query_data.data.reference_task_id);
			if (!find)
			{
				console.warn(
					`Warning: PageParser unable to find task ${query_data.data.reference_task_id}`,
				);
				throw 'Unable to find referenced task ' + query_data.data.reference_task_id;
			}

			const task_route = find.data.task_route;
			return '/_edit' + task_route + '/' + find.data.participant_id;
		}

		// We don't have a reference task id so we need to find a route that knows how to edit
		// the path based on table name
		let re1 = new RegExp(':' + query_data.data.table_name + '.*:Participant');
		let re2 = new RegExp(':Participant.*:' + query_data.data.table_name);

		/** Found a route with edit in the page name specifically */
		for (let route of this.getRouteList())
		{
			if (re1.test(route) || re2.test(route))
			{
				if (/edit/i.test(route))
				{
					console.log('PageParser found edit route:', route);
					let routeText = route.replace(':Participant', query_data.data.participant_id);
					routeText = routeText.replace(
						':' + query_data.data.table_name,
						query_data.data.reference_id,
					);
					routeText = '/_edit' + routeText;
					console.log('PageParser found final edit route:', routeText);
					return routeText;
				}
			}
		}

		/** Found a route as close as possible */
		for (let route of this.getRouteList())
		{
			if (re1.test(route) || re2.test(route))
			{
				console.log('PageParser stage2 found edit route:', route);
				let routeText = route.replace(':Participant', query_data.data.participant_id);
				routeText = routeText.replace(
					':' + query_data.data.table_name,
					query_data.data.reference_id,
				);
				routeText = '/_edit' + routeText;
				console.log('PageParser stage2 found final edit route:', routeText);
				return routeText;
			}
		}

		return '';
	}

	/**
	 * Given a route defined in the configuration file and a route from
	 * the user, check to see if there is a match. This handles table load
	 * replacement variables.  For example,  /page/user/:User would mean that
	 * the 3rd parameter to /page/user/50 is an ID that should load the User record
	 *
	 * @param sourceRouteText
	 * @param testRoute
	 * @returns An object that contains one key for each table that lists the value to load.
	 */
	routeMatch(sourceRouteText: string, testRoute: string): any
	{
		let lastDefaultValue = '';
		const posEquals = sourceRouteText.indexOf('=');
		if (posEquals != -1)
		{
			lastDefaultValue = sourceRouteText.substring(posEquals + 1);
			sourceRouteText = sourceRouteText.substring(0, posEquals);
		}

		sourceRouteText = sourceRouteText.replace(/\/$/g, '');
		sourceRouteText = sourceRouteText.replace(/^\//g, '');

		testRoute = testRoute.replace(/\/$/g, '');
		testRoute = testRoute.replace(/^\//g, '');

		const sourceParts = sourceRouteText.split('/');
		const targetParts = testRoute.split('/');

		if (targetParts.length != sourceParts.length && lastDefaultValue === '') return false;

		if (targetParts.length + 1 != sourceParts.length && targetParts.length != sourceParts.length && lastDefaultValue !== '')
			return false;

		let results: any = {};

		for (let partNum = 0; partNum < sourceParts.length; partNum++)
		{
			if (sourceParts[partNum].startsWith(':'))
			{
				let tableName = sourceParts[partNum].replace(':', '');
				if (targetParts.length <= partNum || targetParts[partNum] === undefined)
				{
					results[tableName] = lastDefaultValue;
				} else
				{
					results[tableName] = targetParts[partNum];
				}
			} else
			{
				// Remove _ from both so that it works with or without
				const strSourcePart = sourceParts[partNum].toLowerCase().replace(/[_ \-]/g, '');
				const strTargetPart = targetParts[partNum].toLowerCase().replace(/[_ \-]/g, '');
				if (strSourcePart != strTargetPart)
				{
					return false;
				}
			}
		}

		return results;
	}

	/**
	 * Loop through all available routes and find ones that have /library/ in the start
	 * return all that match.  These will be auto-executed in each page.
	 */
	async findLibraries(): Promise<PageEngine[]>
	{
		let list: PageEngine[] = [];

		// Loop through each config data and check to see if the
		// route path for a config matches /library/ to start with.
		for (let tableName in this.rawConfigFile.tables)
		{
			for (let tableData of this.rawConfigFile.tables[tableName])
			{
				if (!tableData.meta || !tableData.meta.route) continue;

				// console.log("Match [" + tableData.meta.route + "] to [" + routePath + "]");

				// Make sure we have created a search regexp for this route
				if (tableData.meta.route.startsWith('/library/'))
				{
					let engine = new PageEngine(tableData);
					list.push(engine);
				}
			}
		}

		return list;
	}

	/**
	 * Find a route given a path.
	 * If the path is a full value like /page/something it will match the "route" meta data defined in excell.
	 * This can also match against the tab name on the bottom.
	 */
	async findRoute(routePath: string): Promise<PageEngine>
	{
		// console.log("FIND ROUTE:", routePath);
		/* Special task for task prefix */
		let taskId = '';
		let specialMode = '';

		/** Special case for editing a record based on table name and ID
		 * as found in the Query record.
		 */
		const matchSpecial = routePath.match(/_editquery([a-zA-Z0-9]+)/);
		if (matchSpecial)
		{
			const query_id = matchSpecial[1];
			routePath = await this.findEditLink(query_id);
		}

		/** Special case for task route /_taskSometTaskID.  If found, remove it
		 * and keep the task ID so we can load values for the page engine.
		 */
		const match = routePath.match(/._task([a-zA-Z0-9]+)/);
		if (match)
		{
			taskId = match[1];
			routePath = routePath.replace(match[0], '');
		}

		/**
		 * Special case for edit route /_edit/.  If found, remove it and
		 * set the special mode value to edit.
		 */
		const matchEdit = routePath.match(/._edit/);
		if (matchEdit)
		{
			specialMode = 'edit';
			routePath = routePath.replace(matchEdit[0], '');
		}

		/**
		 * Special case for edit route /_edit/.  If found, remove it and
		 * set the special mode value to edit.
		 */
		const matchViewMode = routePath.match(/._readonly/);
		if (matchViewMode)
		{
			specialMode = 'readonly';
			routePath = routePath.replace(matchViewMode[0], '');
		}

		/**
		 * Special case for edit route /_edit/.  If found, remove it and
		 * set the special mode value to edit.
		 */
		const matchReview = routePath.match(/._review/);
		if (matchReview)
		{
			specialMode = 'review';
			routePath = routePath.replace(matchReview[0], '');
		}

		for (let tableName in this.rawConfigFile.tables)
		{
			for (let tableData of this.rawConfigFile.tables[tableName])
			{
				if (!tableData.meta || !tableData.meta.route) continue;

				// console.log("Match [" + tableData.meta.route + "] to [" + routePath + "]");
				// Make sure we have created a search regexp for this route

				let result = this.routeMatch(tableData.meta.route, routePath);
				if (result !== false)
				{
					// Found a route that matches.  Result might have a set of tables
					// to load based on the route path.
					let engine = new PageEngine(tableData);
					for (let tableName of Object.keys(result))
					{
						if (tableName.charAt(0) === '#')
						{
							//  For a temporary value in the format like /page/name/:#value
							//  assign the value to the logic engine
							engine.logic.assign(tableName.substring(1), result[tableName]);

							if (LogicEngine.debugWebdriver)
								console.log("ASSIGN HEADER:", DataTools.toJSON({ var: tableName.substring(1), val: result[tableName] }));

							//  This might be an in memory table.  If so, load the values into logic engine.
							const memValue = DataRecord.loadFromMemoryById(tableName, result[tableName]);
							if (memValue)
							{
								engine.logic.import(memValue.data);
								if (LogicEngine.debugWebdriver)
									console.log("ASSIGN HEADER:", DataTools.toJSON(memValue.data));
							}
						} else
						{
							//  Load the table name such as
							//  /page/participant/:participant  -- use the participant table to load the value
							let loadResult = await engine.loadRecord(tableName, result[tableName]);
							if (!loadResult)
							{
								console.warn('Critical Error loading record for route ', routePath);
								console.warn('Returning empty page because loadRecord(', tableName, ',', result[tableName], ') failed.',);
								return new PageEngine({ tableName: '', meta: {}, data: [], });
								// TODO How to handle this when the ID to a route is not valid.
							}
						}
					}

					// Data monitor mode instead of edit
					if (specialMode === 'edit' && engine.logic.fnHasGroup('DataMonitor'))
					{
						specialMode = 'query';
					}

					// Data monitor mode instead of review
					if (specialMode === 'review' && engine.logic.fnHasGroup('DataMonitor'))
					{
						specialMode = 'query';
					}

					// Special mode currently is "" or "edit"
					engine.logic.assign('special_mode', specialMode);

					if (taskId !== '')
					{
						// Save task id to logic engine, other things in the future?
						engine.logic.assign('task_id', taskId);
						PageParser.last_task_id = taskId;

						// Load the task- will throw exception if the task is invalid
						const taskManager = new TaskManager();
						let taskRecord = await taskManager.getTaskDataById(taskId);
						engine.logic.assign('task_title', taskRecord.record.data.title_en);
						engine.logic.assign('task_status', taskRecord.record.data.task_status);

						if (LogicEngine.debugWebdriver)
						{
							console.log("ASSIGN HEADER:", DataTools.toJSON({ var: "task_title", val: taskRecord.record.data.title_en }));
							console.log("ASSIGN HEADER:", DataTools.toJSON({ var: "task_id", val: taskId }));
							console.log("ASSIGN HEADER:", DataTools.toJSON({ var: "task_status", val: taskRecord.record.data.task_status }));
						}

						if (LogicEngine.debugOutput)
							console.log('PageParser findRoute Found existing task id=', taskId, ' details=', taskRecord.record.data,);
					} else
					{
						engine.logic.assign('task_id', '');
						engine.logic.assign('task_title', '');
						engine.logic.assign('task_status', '');

						if (LogicEngine.debugWebdriver)
						{
							console.log("ASSIGN HEADER:", DataTools.toJSON({ var: "task_title", val: "" }));
							console.log("ASSIGN HEADER:", DataTools.toJSON({ var: "task_id", val: "" }));
							console.log("ASSIGN HEADER:", DataTools.toJSON({ var: "task_status", val: "" }));
						}
					}

					// Last task ID that was loaded
					engine.logic.assign('last_task_id', PageParser.last_task_id);

					return engine;
				}
			}
		}

		return new PageEngine({
			tableName: '',
			meta: {},
			data: [],
		});
	}

	/**
	 * Search Commands from Page
	 *
	 * @param searchPattern
	 * @returns
	 */
	search(searchPattern: string | RegExp): SearchResultSheets
	{
		let result: SearchResultSheets = {};
		for (const sheetName in this.rawConfigFile.tables)
		{
			let sheetResult: SearchResultTable[] = [];
			for (const tableData of this.rawConfigFile.tables[sheetName])
			{
				const foundedCommands =
					tableData.data?.filter((e: PageStepType) =>
					{
						if (typeof searchPattern === 'string')
						{
							searchPattern = new RegExp(`${searchPattern}`, 'i');
						}
						const searchResult = Object.values(e).join(' ').search(searchPattern);
						return searchResult === -1 ? false : true;
					}) ?? [];
				sheetResult = [
					...sheetResult,
					...(foundedCommands.length
						? [
							{
								tableName: tableData.tableName,
								pageStepTypes: foundedCommands,
							},
						]
						: []),
				];
			}

			result = {
				...result,
				...(sheetResult.length ? { [sheetName]: [...sheetResult] } : {}),
			};
		}

		return result;
	}
}
